import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import Image from 'next/image';
import Link from 'next/link';

interface InvestorDetailsCardProps {
  logo: string;
  name: string;
  tagline: string;
  description: string;
}

const InvestorDetailsCard = ({
  logo,
  name,
  tagline,
  description
}: InvestorDetailsCardProps): JSX.Element => {
  return (
    <Flex
      w="full"
      h="150px"
      bg="white"
      p="24px"
      justifyContent={'center'}
      alignItems="center"
      rounded="lg"
      boxShadow={'0px 2px 6px rgba(57, 67, 86, 0.08)'}
    >
      <Flex w="397.1">
        <Box pr="16px" pt={1}>
          <Image src={logo} alt="" height={72} width={72} />
        </Box>
        <Flex
          flexDir="column"
          alignContent="space-between"
          justifyContent={'space-between'}
          h="full"
          letterSpacing={'-0.02em'}
          fontFamily={'heading'}
          minH="95px"
        >
          <Heading
            as={'h2'}
            fontSize={'16px'}
            lineHeight="24px"
            fontWeight={700}
          >
            {name}
          </Heading>
          <Text
            fontSize="12px"
            lineHeight={'15px'}
            maxW={'261px'}
            fontWeight={500}
            minH="45px"
          >
            {tagline}
          </Text>
          <Text
            fontWeight={300}
            lineHeight="12px"
            mt="6px"
            fontSize={'12px'}
            fontStyle="normal"
          >
            {description}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

const InvestorLogoCard = ({ logo }: { logo: string }): JSX.Element => {
  return (
    <Flex
      h="80px"
      w="full"
      maxW="195px"
      bg="white"
      px="24px"
      py="9px"
      justifyContent={'center'}
      alignItems="center"
      rounded="lg"
      mt="19px"
      mb="40px"
      boxShadow={'0px 2px 6px rgba(57, 67, 86, 0.08)'}
    >
      {' '}
      <Image src={logo} alt="" height={62} width={148} />
    </Flex>
  );
};

interface InvestorProfileCardProps {
  image: string;
  name: string;
  title: string;
  social: string;
}

const InvestorProfileCard = ({
  image,
  name,
  title,
  social
}: InvestorProfileCardProps): JSX.Element => {
  return (
    <Flex
      bg="white"
      p="20px"
      w="180px"
      flexDir={'column'}
      justifyContent="center"
      alignItems="center"
      letterSpacing={'-0.02em'}
      rounded="lg"
      boxShadow={'0px 2px 6px rgba(57, 67, 86, 0.08)'}
    >
      <Box rounded="full" overflow={'hidden'} mx="44px" mb="16px">
        <Image src={image} alt="" width={64} height={64} />
      </Box>
      {social ? (
        <Link href={social}>
          <Text
            fontWeight={700}
            fontFamily={'heading'}
            fontSize="14px"
            color="black"
          >
            {name}
          </Text>
        </Link>
      ) : (
        <Text
          fontWeight={700}
          fontFamily={'heading'}
          fontSize="14px"
          color="black"
        >
          {name}
        </Text>
      )}
      <Text fontFamily={'heading'} whiteSpace={'nowrap'}>
        {title}
      </Text>
    </Flex>
  );
};

export const Investors = (): JSX.Element => {
  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      flexDir={'column'}
      pt={{ base: '60px', md: 0 }}
    >
      <Box pos="relative">
        <Heading size="h1" textAlign="center" mb="60px">
          Funded by industry-leading investors.
        </Heading>
      </Box>
      <Flex
        flexDir={{ base: 'column', lg: 'row' }}
        gap={'32px'}
        mb="16px"
        zIndex={10}
      >
        <InvestorDetailsCard
          logo={'/images/logos/madrona.svg'}
          name="Madrona"
          tagline="Backing founding teams in seed, early, and acceleration stages in the Pacific Northwest and beyond."
          description="Investor in Amazon, Branch, and more."
        />
        <InvestorDetailsCard
          logo={'/images/logos/a16z.svg'}
          name="Andreessen Horowitz"
          tagline={`Backing bold entrepreneurs building the future through technology.`}
          description="Investor in Lyft, Slack, Github, and more."
        />
        <InvestorDetailsCard
          logo={'/images/logos/s28.svg'}
          name="S28 Capital"
          tagline={`A venture capital firm focused on investing in companies disrupting long-standing industries.`}
          description="Investor in Rippling, Vercel, Bricks, and more."
        />
      </Flex>

      <Flex
        flexWrap={'wrap'}
        gap={{ base: '6px', lg: '16px' }}
        zIndex={10}
        justifyContent="center"
        mt={{ base: '12px', lg: '' }}
      >
        <InvestorLogoCard logo={'/images/logos/fathom.svg'} />
        <InvestorLogoCard logo={'/images/logos/southparkcommons.svg'} />
        <InvestorLogoCard logo={'/images/logos/mango.svg'} />
        <InvestorLogoCard logo={'/images/logos/netlify.svg'} />
      </Flex>

      <Heading pt={'80px'} pb="60px" textAlign={'center'}>
        Individual investors
      </Heading>

      <Flex flexWrap={'wrap'} gap={'24px'} justifyContent="center">
        <InvestorProfileCard
          image="/avatars/investors/guillermo-rauch.svg"
          name="Guillermo Rauch"
          title="Founder of Vercel"
          social="https://twitter.com/rauchg"
        />
        <InvestorProfileCard
          image="/avatars/investors/andrew-miklas.svg"
          name="Andrew Miklas"
          title="Founder of PagerDuty"
          social=""
        />
        <InvestorProfileCard
          image="/avatars/investors/darragh-buckley.svg"
          name="Darragh Buckley"
          title="Founder of Increase"
          social=""
        />
        <InvestorProfileCard
          image="/avatars/investors/ilya-volodarsky.svg"
          name="Ilya Volodarsky"
          title="Founder of Segment"
          social="https://twitter.com/ivolo"
        />
        <InvestorProfileCard
          image="/avatars/investors/calvin-french-owen.svg"
          name="Calvin French-Owen"
          title="Founder of Segment"
          social="https://twitter.com/calvinfo"
        />
        <InvestorProfileCard
          image="/avatars/investors/gordon-wintrob.svg"
          name="Gordon Wintrob"
          title="Founder of Newfront"
          social="https://twitter.com/gwintrob"
        />
        <InvestorProfileCard
          image="/avatars/investors/max-kanter.svg"
          name="Max Kanter"
          title="Creator of FeatureTools"
          social="https://twitter.com/maxk"
        />
        <InvestorProfileCard
          image="/avatars/investors/dave-wilner.svg"
          name="Dave Wilner"
          title="CRO of Auth0"
          social=""
        />
        <InvestorProfileCard
          image="/avatars/investors/max-stoiber.svg"
          name="Max Stoiber"
          title="Founder of Stellate"
          social="https://twitter.com/mxstbr"
        />
        <InvestorProfileCard
          image="/avatars/investors/laduram-vishnoi.svg"
          name="Ladurum Vishnoi"
          title="Founder of Middleware"
          social="https://twitter.com/laduramvishnoi"
        />
        <InvestorProfileCard
          image="/avatars/investors/amrit-dhangal.svg"
          name="Amrit Dhangal"
          title="Founder of Acquire"
          social="https://twitter.com/AmritDhangal"
        />
      </Flex>
    </Flex>
  );
};
